import { useMemo, memo } from "react";
import { Platform } from "react-native";
import { PressableScale } from "pressto";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { findNextTransitionOptimized } from "@src/features/alarms/utils/alarm-actions";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import { getCameraKeyString, getCameraDisplayName, isLegacyAppCamera } from "@src/utils/key-parser";
import type { CameraApiResponse } from "@src/features/cameras";
import { useCameraArmedStatus } from "@src/features/cameras/hooks/useCameraArmedStatus";
import { Icon, IconNameType } from "@src/components/Icon";
import { capitalize } from "@src/utils/capitalize";
import type { Href } from "expo-router";
import { NavPressable } from "@src/components/NavPressable";
import { SignedSnapshot, SnapshotImage } from "@visioni/a3s-media/app";
import { LegacySnapshotImage } from "./LegacySnapshotImage";
import { sizes } from "@src/constants/sizes";

export interface CameraListItemSidepanelProps {
  camera: CameraApiResponse;
  snapshot?: SignedSnapshot | null;
  siteId: string;
  onSelect: (cameraKey: string) => void;
  href?: Href;
  /** When true, applies selected state styling (e.g. for side panel context) */
  isSelected?: boolean;
  collapsed?: boolean;
}

export const CameraListItemSidepanel = memo(
  ({
    camera,
    siteId,
    onSelect,
    href,
    isSelected = false,
    snapshot,
    collapsed = false,
  }: CameraListItemSidepanelProps) => {
    const { theme } = useUnistyles();
    const cameraKey = getCameraKeyString(camera);
    const { alarmDetail, isArmed } = useCameraArmedStatus({ camera, siteId });

    const isPaused = false; // TODO: API pending

    const scheduleTime = useMemo(() => {
      if (!alarmDetail) return null;
      const nextTransition = findNextTransitionOptimized({ alarmDetail });
      if (!nextTransition) return null;

      const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";
      const transitionDateTime = dateTimeRecordToLocalisedDateTime(nextTransition.datetimeRecord, timezone);

      const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const day = dayNames[transitionDateTime.weekday - 1];

      const hours = transitionDateTime.hour;
      const minutes = transitionDateTime.minute;
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes.toString().padStart(2, "0");

      const action = nextTransition.toState === "armed" ? "Arms" : "Disarms";
      return `${action} ${day} ${formattedHours}:${formattedMinutes} ${ampm}`;
    }, [alarmDetail]);

    const displayName = camera.name || getCameraDisplayName(cameraKey);

    const cameraIdDisplay = isLegacyAppCamera(camera) ? `Camera ID: ${camera.key.id}` : cameraKey;

    const thumbnailHeight = 82;

    const statusBadge = useMemo(() => {
      if (isPaused) {
        return {
          label: "Paused",
          iconName: "pause" as IconNameType,
          backgroundColor: theme.colors.grey[500],
          borderColor: theme.colors.grey[400],
        };
      }
      if (isArmed) {
        return {
          label: "Armed",
          iconName: "lockClosed" as IconNameType,
          backgroundColor: theme.colors.purple[600],
          borderColor: theme.colors.purple[300],
        };
      }
      return {
        label: "Disarmed",
        iconName: "lockOpen" as IconNameType,
        backgroundColor: theme.colors.warning[700],
        borderColor: theme.colors.warning[400],
      };
    }, [isPaused, isArmed, theme]);

    const content = (
      <View flexDirection="row" alignItems="center" gap="md" justifyContent="center" py="xl">
        {/* Camera Thumbnail */}
        {snapshot ?
          <SnapshotImage
            snapshot={snapshot}
            width={400}
            style={styles.thumbnail}
            placeholderColor={theme.colors.borderCard}
          />
        : <LegacySnapshotImage
            cameraKey={cameraKey}
            thumbnailUrl={camera.snapshot?.url}
            capturedAt={camera.snapshot?.capturedAt}
            style={{ width: 102, height: 72, borderRadius: theme.spacing.sm }}
          />
        }

        {/* Camera Info */}
        {!collapsed && (
          <View gap="xs">
            <View>
              <View flex={1}>
                <Text textStyle="sm/regular" numberOfLines={1} adjustsFontSizeToFit>
                  {capitalize(displayName)}
                </Text>
                <Text textStyle="xxs/regular" mt="xs" numberOfLines={1} style={styles.textSecondary}>
                  {cameraIdDisplay}
                </Text>
              </View>
            </View>

            {/* <View borderWidth={1} borderColor="muted" my="3xl" /> */}

            <View
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="xl"
              style={styles.containerCameraStatus}
            >
              {/* Armed/Disarmed Status */}
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: statusBadge.backgroundColor, borderColor: statusBadge.borderColor },
                ]}
              >
                <Icon name={statusBadge.iconName} size={14} color={theme.colors.white.default} />
                <Text textStyle="xxs/regular" style={styles.textBadge}>
                  {statusBadge.label}
                </Text>
              </View>
            </View>
          </View>
        )}
      </View>
    );
    if (href) {
      return (
        <NavPressable
          onPress={() => onSelect(cameraKey)}
          href={href}
          style={[
            styles.container,
            {
              backgroundColor: isSelected ? theme.colors.surfaceCardInner : theme.colors.surface,
              borderColor: isSelected ? theme.colors.borderCard : theme.colors.border,
            },
          ]}
        >
          {content}
        </NavPressable>
      );
    }

    return (
      <PressableScale
        onPress={() => onSelect(cameraKey)}
        style={[
          styles.container,
          {
            backgroundColor: isSelected ? theme.colors.surfaceCardInner : theme.colors.surface,
            borderColor: isSelected ? theme.colors.borderCard : theme.colors.border,
          },
        ]}
      >
        {content}
      </PressableScale>
    );
  },
  (prevProps, nextProps) => {
    const cameraKeyEqual =
      prevProps.camera.key.id === nextProps.camera.key.id && prevProps.camera.key.asset === nextProps.camera.key.asset;
    const snapshotEqual = prevProps.snapshot?.url === nextProps.snapshot?.url;
    const legacySnapshotEqual =
      prevProps.camera.snapshot?.url === nextProps.camera.snapshot?.url &&
      prevProps.camera.snapshot?.capturedAt === nextProps.camera.snapshot?.capturedAt;
    const siteIdEqual = prevProps.siteId === nextProps.siteId;
    // Compare alarmSettings to ensure re-render when alarm state changes
    const alarmSettingsEqual =
      JSON.stringify(prevProps.camera.alarmSettings) === JSON.stringify(nextProps.camera.alarmSettings);
    const isSelectedEqual = prevProps.isSelected === nextProps.isSelected;
    const hrefEqual = prevProps.href === nextProps.href;

    return (
      cameraKeyEqual &&
      snapshotEqual &&
      legacySnapshotEqual &&
      siteIdEqual &&
      alarmSettingsEqual &&
      isSelectedEqual &&
      hrefEqual
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.spacing.md,
    ...(Platform.OS === "web" ? {} : { ...theme.shadows.md }),
  },
  thumbnailContainer: {
    // width: "100%",
    width: 100,
    position: "relative",

    overflow: "hidden",
    backgroundColor: theme.colors.borderCard,
  },
  thumbnail: {
    width: "100%",
    height: "100%",
  },
  thumbnailPlaceholder: {
    width: "100%",
    height: "100%",
  },
  containerCameraInfo: {},
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.spacing["3xl"],
    borderWidth: 1,
    height: 26,
  },
  containerCameraStatus: {
    overflow: "hidden",
  },
  textBadge: {
    color: theme.colors.white.default,
  },
  textSecondary: {
    color: theme.colors.surface1.foregroundSecondary,
  },
}));
